import React, { useMemo, useState } from 'react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const canSubmit = useMemo(() => {
    return username.trim().length > 0 && password.length > 0 && !submitting;
  }, [username, password, submitting]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setSubmitting(true);

    const url = 'api/login.php';

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ username, password }),
        credentials: 'include',
      });

      const text = await res.text();
      let data: any;

      try {
        data = JSON.parse(text);
      } catch {
        console.error('NON-JSON from:', url);
        console.error('Status:', res.status);
        console.error('Response text:', text);
        setError(`API trả HTML (không phải JSON). Kiểm tra rewrite/.htaccess. Status ${res.status}`);
        return;
      }

      if (data.success) {
        window.location.reload();
      } else {
        setError(data.message || 'Sai tài khoản hoặc mật khẩu');
      }
    } catch (err) {
      console.error(err);
      setError('Không thể kết nối server');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background accents */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-emerald-200/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-indigo-200/40 blur-3xl" />
      </div>

      {/* Form đăng nhập căn giữa */}
      <div className="relative w-full max-w-md rounded-3xl bg-white/95 backdrop-blur border border-slate-200/80 shadow-xl p-6 sm:p-10 z-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase">SANG PHARMA PORTAL</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-montserrat font-bold text-slate-800">
              Đăng nhập
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Vui lòng nhập tài khoản để tiếp tục.
            </p>
          </div>

          <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
            <i className="fas fa-lock text-lg"></i>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-xs sm:text-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-rose-600">
                <i className="fas fa-triangle-exclamation"></i>
              </div>
              <div>
                <p className="font-semibold">Không đăng nhập được</p>
                <p className="mt-0.5">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Tên đăng nhập</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400 pointer-events-none">
                <i className="fas fa-user"></i>
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder=""
                autoComplete="username"
                required
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white px-12 py-3.5 text-slate-800 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-emerald-500/15 focus:border-emerald-500 transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Mật khẩu</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400 pointer-events-none">
                <i className="fas fa-key"></i>
              </span>

              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
                autoComplete="current-password"
                required
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white px-12 py-3.5 pr-12 text-slate-800 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-emerald-500/15 focus:border-emerald-500 transition"
              />

              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-600"
                aria-label={showPw ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                <i className={`fas ${showPw ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full rounded-2xl py-3.5 font-bold text-white shadow-lg transition flex items-center justify-center gap-2
                ${canSubmit ? 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 shadow-emerald-600/20 cursor-pointer' : 'bg-slate-300 cursor-not-allowed shadow-none'}
              `}
            >
              {submitting ? (
                <>
                  <span className="inline-block w-4 h-4 rounded-full border-2 border-white/70 border-t-transparent animate-spin"></span>
                  <span>Đang đăng nhập...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-right-to-bracket"></i>
                  <span>Đăng nhập</span>
                </>
              )}
            </button>
          </div>

          <div className="text-center text-slate-400 text-xs mt-6 pt-2">
            © {new Date().getFullYear()} Sang Pharma. All rights reserved.
          </div>
        </form>
      </div>
    </div>
  );
}